import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tag, Check } from 'lucide-react';
import { ConfirmDialog, EmptyState, Modal, PageLoader, Spinner } from '../components/ui';
import { Seo } from '../components/Seo';
import { useToast } from '../components/Toast';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { accountApi } from '../api/account';
import type { CustomerCouponDto } from '../api/types';
import { money } from '../lib/format';

export function CartPage() {
  const cart = useCart();
  const toast = useToast();
  const { isAuthenticated } = useAuth();
  const [discountCode, setDiscountCode] = useState('');
  const [giftCardCode, setGiftCardCode] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [couponModalOpen, setCouponModalOpen] = useState(false);

  const { data: myCoupons = [] } = useQuery<CustomerCouponDto[]>({
    queryKey: ['account', 'coupons'],
    queryFn: () => accountApi.coupons(),
    enabled: isAuthenticated,
  });

  const activeCoupons = myCoupons.filter((c) => c.status === 'Active');

  if (cart.isLoading) return <PageLoader label="Sepetiniz yükleniyor…" />;

  const data = cart.cart;

  if (!data || data.items.length === 0) {
    return (
      <div className="container-page py-16">
        <EmptyState
          title="Sepetiniz boş"
          description="Beğendiğiniz ürünleri sepete ekleyerek alışverişe başlayabilirsiniz."
          action={
            <Link to="/urunler" className="btn-primary">
              Ürünlere göz at
            </Link>
          }
        />
      </div>
    );
  }

  const apply = async (action: () => Promise<void>, successMessage: string) => {
    await action();
    toast.success(successMessage);
  };

  return (
    <div className="container-page py-8">
      <Seo title="Alışveriş Sepetim" noIndex />
      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-ink-900">Sepetim</h1>

      {data.blockingErrors.length > 0 ? (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3" role="alert">
          <p className="text-sm font-medium text-red-800">Ödemeye geçmeden önce çözülmeli</p>
          <ul className="mt-1 space-y-0.5 text-sm text-red-700">
            {data.blockingErrors.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {data.warnings.length > 0 ? (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <ul className="space-y-0.5 text-sm text-amber-800">
            {data.warnings.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
        <div>
          <ul className="divide-y divide-ink-100 rounded-xl border border-ink-200">
            {data.items.map((line) => (
              <li key={line.id} className="flex gap-4 p-4">
                <Link
                  to={line.productSlug ? `/urun/${line.productSlug}` : '#'}
                  className="h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-ink-50"
                >
                  {line.imageUrl ? (
                    <img src={line.imageUrl} alt="" className="h-full w-full object-cover" />
                  ) : null}
                </Link>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      {line.brandName ? (
                        <p className="text-xs uppercase tracking-wide text-ink-400">{line.brandName}</p>
                      ) : null}
                      <p className="truncate text-sm font-medium text-ink-900">
                        <Link to={line.productSlug ? `/urun/${line.productSlug}` : '#'}>
                          {line.productTitle}
                        </Link>
                      </p>
                      {line.variantTitle ? (
                        <p className="text-xs text-ink-500">{line.variantTitle}</p>
                      ) : null}
                      <p className="mt-0.5 text-xs text-ink-400">{line.sku}</p>
                    </div>

                    <p className="text-sm font-semibold text-ink-900">
                      {money(line.lineTotal, data.currencyCode)}
                    </p>
                  </div>

                  {!line.isAvailable ? (
                    <p className="mt-2 text-xs font-medium text-red-600">
                      {line.unavailableReason ?? 'Bu ürün şu anda satın alınamıyor.'}
                    </p>
                  ) : null}

                  {line.priceChangedFrom ? (
                    <p className="mt-2 text-xs text-amber-700">
                      Fiyat {money(line.priceChangedFrom, data.currencyCode)} iken{' '}
                      {money(line.unitPrice, data.currencyCode)} oldu.
                    </p>
                  ) : null}

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <div className="flex items-center rounded-lg border border-ink-200">
                      <button
                        type="button"
                        className="px-2.5 py-1.5 text-ink-600 disabled:opacity-40"
                        disabled={cart.busy || line.quantity <= 1}
                        onClick={() =>
                          void cart.updateItem(line.id, { quantity: line.quantity - 1 })
                        }
                        aria-label="Adedi azalt"
                      >
                        −
                      </button>
                      <span className="w-9 text-center text-sm">{line.quantity}</span>
                      <button
                        type="button"
                        className="px-2.5 py-1.5 text-ink-600 disabled:opacity-40"
                        disabled={
                          cart.busy ||
                          (line.availableQuantity !== null &&
                            line.availableQuantity !== undefined &&
                            line.quantity >= line.availableQuantity)
                        }
                        onClick={() =>
                          void cart.updateItem(line.id, { quantity: line.quantity + 1 })
                        }
                        aria-label="Adedi artır"
                      >
                        +
                      </button>
                    </div>

                    <span className="text-xs text-ink-500">
                      Birim {money(line.unitPrice, data.currencyCode)}
                    </span>

                    <button
                      type="button"
                      className="ml-auto text-xs text-red-600 hover:underline"
                      disabled={cart.busy}
                      onClick={() => void cart.removeItem(line.id)}
                    >
                      Kaldır
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Link to="/urunler" className="btn-secondary btn-sm">
              Alışverişe devam et
            </Link>
            <button
              type="button"
              className="btn-ghost btn-sm text-red-600"
              onClick={() => setConfirmClear(true)}
              disabled={cart.busy}
            >
              Sepeti boşalt
            </button>
          </div>

          {/* Siparis notu */}
          <div className="card mt-6 p-5">
            <label className="label" htmlFor="cart-note">
              Sipariş notu
            </label>
            <textarea
              id="cart-note"
              className="field min-h-20"
              maxLength={1000}
              placeholder="Teslimat veya paketleme ile ilgili notunuz"
              value={note ?? data.note ?? ''}
              onChange={(event) => setNote(event.target.value)}
            />
            <button
              type="button"
              className="btn-secondary btn-sm mt-2"
              disabled={cart.busy || note === null}
              onClick={() => void apply(() => cart.setNote({ note }), 'Not kaydedildi.')}
            >
              Notu kaydet
            </button>
          </div>
        </div>

        {/* Ozet */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="card p-5">
            <h2 className="text-base font-semibold text-ink-900">Sipariş özeti</h2>

            <dl className="mt-4 space-y-2.5 text-sm">
              <Row label="Ara toplam" value={money(data.subTotal, data.currencyCode)} />
              {data.discountTotal > 0 ? (
                <Row
                  label="İndirim"
                  value={`− ${money(data.discountTotal, data.currencyCode)}`}
                  tone="success"
                />
              ) : null}
              <Row
                label="Kargo"
                value={
                  data.shippingTotal > 0
                    ? money(data.shippingTotal, data.currencyCode)
                    : 'Ödeme adımında'
                }
              />
              {data.shippingDiscountTotal > 0 ? (
                <Row
                  label="Kargo indirimi"
                  value={`− ${money(data.shippingDiscountTotal, data.currencyCode)}`}
                  tone="success"
                />
              ) : null}
              {data.taxLines.map((tax) => (
                <Row
                  key={tax.title}
                  label={`${tax.title} (%${tax.rate})`}
                  value={money(tax.amount, data.currencyCode)}
                  muted={tax.isIncludedInPrice}
                />
              ))}
              {data.giftCardTotal > 0 ? (
                <Row
                  label="Hediye kartı"
                  value={`− ${money(data.giftCardTotal, data.currencyCode)}`}
                  tone="success"
                />
              ) : null}
            </dl>

            <div className="mt-4 flex items-baseline justify-between border-t border-ink-200 pt-4">
              <span className="text-sm font-medium text-ink-700">Ödenecek tutar</span>
              <span className="text-xl font-semibold text-ink-900">
                {money(data.amountDue, data.currencyCode)}
              </span>
            </div>

            {data.remainingForFreeShipping && data.remainingForFreeShipping > 0 ? (
              <p className="mt-3 rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-800">
                Ücretsiz kargo için {money(data.remainingForFreeShipping, data.currencyCode)} daha
                ekleyin.
              </p>
            ) : null}

            <Link
              to="/odeme"
              className={`btn-primary mt-5 w-full ${data.canCheckout ? '' : 'pointer-events-none opacity-50'}`}
              aria-disabled={!data.canCheckout}
            >
              Ödemeye geç
            </Link>
          </div>

          {/* Indirim kodu */}
          <div className="card mt-4 p-5">
            <h3 className="text-sm font-semibold text-ink-900">İndirim kodu</h3>

            {data.discounts.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {data.discounts.map((discount, index) => (
                  <li
                    key={`${discount.code ?? discount.description}-${index}`}
                    className="flex items-center justify-between gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate text-emerald-800">
                      {discount.code ?? discount.description}
                    </span>
                    <span className="text-emerald-700">
                      − {money(discount.amount, data.currencyCode)}
                    </span>
                    {discount.code && !discount.isAutomatic ? (
                      <button
                        type="button"
                        className="text-xs text-emerald-700 underline"
                        disabled={cart.busy}
                        onClick={() =>
                          void apply(
                            () => cart.removeDiscountCode(discount.code!),
                            'İndirim kodu kaldırıldı.',
                          )
                        }
                      >
                        Kaldır
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}

            <form
              className="mt-3 flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (!discountCode.trim()) return;
                void apply(
                  () => cart.applyDiscountCode({ code: discountCode.trim() }),
                  'İndirim kodu uygulandı.',
                ).then(() => setDiscountCode(''));
              }}
            >
              <input
                className="field py-2 text-sm uppercase"
                placeholder="KOD"
                value={discountCode}
                onChange={(event) => setDiscountCode(event.target.value)}
                aria-label="İndirim kodu"
              />
              <button type="submit" className="btn-secondary btn-sm shrink-0" disabled={cart.busy}>
                {cart.busy ? <Spinner className="h-4 w-4" /> : 'Uygula'}
              </button>
            </form>

            {isAuthenticated && activeCoupons.length > 0 && (
              <button
                type="button"
                onClick={() => setCouponModalOpen(true)}
                className="mt-2.5 flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:text-brand-900 transition bg-brand-50/70 border border-brand-200 rounded-lg px-2.5 py-1.5 w-full justify-center"
              >
                <Tag className="h-3.5 w-3.5 text-brand-600" />
                <span>Kayıtlı Kuponlarımdan Seç</span>
                <span className="rounded-full bg-brand-200 px-1.5 py-0.2 text-[10px] font-bold text-brand-800">
                  {activeCoupons.length}
                </span>
              </button>
            )}
          </div>

          {/* Hediye karti */}
          <div className="card mt-4 p-5">
            <h3 className="text-sm font-semibold text-ink-900">Hediye kartı</h3>
            <p className="hint">Hediye kartı bir ödeme yöntemidir, KDV matrahını düşürmez.</p>

            {data.giftCards.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {data.giftCards.map((card) => (
                  <li
                    key={card.giftCardId}
                    className="flex items-center justify-between gap-2 rounded-lg bg-ink-50 px-3 py-2 text-sm"
                  >
                    <span className="font-mono text-xs text-ink-700">{card.maskedCode}</span>
                    <span className="text-ink-700">
                      − {money(card.appliedAmount, data.currencyCode)}
                    </span>
                    <button
                      type="button"
                      className="text-xs text-red-600 underline"
                      disabled={cart.busy}
                      onClick={() =>
                        void apply(
                          () => cart.removeGiftCard(card.giftCardId),
                          'Hediye kartı kaldırıldı.',
                        )
                      }
                    >
                      Kaldır
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            <form
              className="mt-3 flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (!giftCardCode.trim()) return;
                void apply(
                  () => cart.applyGiftCard({ code: giftCardCode.trim() }),
                  'Hediye kartı tanımlandı.',
                ).then(() => setGiftCardCode(''));
              }}
            >
              <input
                className="field py-2 text-sm"
                placeholder="Hediye kartı kodu"
                value={giftCardCode}
                onChange={(event) => setGiftCardCode(event.target.value)}
                aria-label="Hediye kartı kodu"
              />
              <button type="submit" className="btn-secondary btn-sm shrink-0" disabled={cart.busy}>
                Ekle
              </button>
            </form>
          </div>
        </aside>
      </div>

      <ConfirmDialog
        open={confirmClear}
        title="Sepeti boşalt"
        message="Sepetinizdeki tüm ürünler kaldırılacak. Devam edilsin mi?"
        confirmLabel="Boşalt"
        danger
        busy={cart.busy}
        onCancel={() => setConfirmClear(false)}
        onConfirm={() => {
          void cart.clear().then(() => {
            setConfirmClear(false);
            toast.success('Sepet boşaltıldı.');
          });
        }}
      />

      <Modal
        open={couponModalOpen}
        onClose={() => setCouponModalOpen(false)}
        title="Kayıtlı Kuponlarım"
        description="Hesabınıza tanımlanmış kuponlardan dilediğinizi tek tıkla uygulayabilirsiniz."
        size="md"
      >
        <div className="space-y-3 max-h-[60vh] overflow-y-auto py-1">
          {activeCoupons.length === 0 ? (
            <div className="p-4 text-center text-sm text-ink-500">
              Şu anda kullanıma uygun aktif bir kuponunuz bulunmuyor.
            </div>
          ) : (
            activeCoupons.map((coupon) => {
              const isApplied = data.discounts.some(
                (d) => d.code && d.code.toUpperCase() === coupon.code.toUpperCase(),
              );
              const subtotal = data.subTotal;
              const meetsSubtotal =
                !coupon.minimumSubtotal || coupon.minimumSubtotal <= 0 || subtotal >= coupon.minimumSubtotal;
              const missingAmount =
                coupon.minimumSubtotal && coupon.minimumSubtotal > subtotal
                  ? coupon.minimumSubtotal - subtotal
                  : 0;

              return (
                <div
                  key={coupon.id}
                  className={`rounded-xl border p-3.5 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                    isApplied
                      ? 'border-emerald-300 bg-emerald-50/50'
                      : meetsSubtotal
                        ? 'border-brand-200 bg-white hover:border-brand-400'
                        : 'border-ink-200 bg-ink-50/40 opacity-80'
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-ink-900">
                        {coupon.type === 'Percentage'
                          ? `%${coupon.value} İndirim`
                          : `${money(coupon.value, data.currencyCode)} İndirim`}
                      </span>
                      <span className="font-mono text-xs font-semibold bg-ink-100 text-ink-700 px-2 py-0.5 rounded border border-ink-200">
                        {coupon.code}
                      </span>
                    </div>
                    <p className="text-xs text-ink-600 mt-1">{coupon.title}</p>

                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink-500">
                      {coupon.minimumSubtotal && coupon.minimumSubtotal > 0 ? (
                        <span className={meetsSubtotal ? 'text-brand-700 font-medium' : 'text-amber-700 font-medium'}>
                          Alt Limit: {money(coupon.minimumSubtotal, data.currencyCode)}
                          {!meetsSubtotal && ` (${money(missingAmount, data.currencyCode)} daha ekleyin)`}
                        </span>
                      ) : (
                        <span className="text-emerald-700">Alt limit yok</span>
                      )}
                      {coupon.entitledCategoryNames && coupon.entitledCategoryNames.length > 0 && (
                        <span>Geçerli: {coupon.entitledCategoryNames.join(', ')}</span>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0">
                    {isApplied ? (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-800">
                        <Check className="h-3.5 w-3.5" />
                        Uygulandı
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={!meetsSubtotal || cart.busy}
                        onClick={() => {
                          void apply(
                            () => cart.applyDiscountCode({ code: coupon.code }),
                            'Kupon başarıyla uygulandı.',
                          ).then(() => setCouponModalOpen(false));
                        }}
                        className={`btn-sm rounded-lg text-xs font-semibold px-3 py-1.5 transition ${
                          meetsSubtotal
                            ? 'btn-primary'
                            : 'bg-ink-200 text-ink-400 cursor-not-allowed'
                        }`}
                      >
                        {meetsSubtotal ? 'Kuponu Uygula' : 'Sepet Yetersiz'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Modal>
    </div>
  );
}

function Row({
  label,
  value,
  tone,
  muted,
}: {
  label: string;
  value: string;
  tone?: 'success';
  muted?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className={muted ? 'text-ink-400' : 'text-ink-600'}>
        {label}
        {muted ? ' (fiyata dahil)' : ''}
      </dt>
      <dd className={tone === 'success' ? 'text-emerald-700' : 'text-ink-900'}>{value}</dd>
    </div>
  );
}
